import React, { useState, useEffect, useMemo } from 'react';
import { formatEuro, formatNumber } from '../../utils/currency';
import { formatDate } from '../../utils/date';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TimelineIcon from '@mui/icons-material/Timeline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { getStocks, StockDto, DividendDto, createDividend, updateDividend, deleteDividend } from '../../services/stocksApi';
import PageHeader from '../../components/layout/PageHeader';
import TabPanel from '../../components/layout/TabPanel';
import AutoGrid from '../../components/layout/AutoGrid';

interface DividendAnalysis {
  symbol: string;
  totalGross: number;
  totalNet: number;
  totalTax: number;
  paymentsCount: number;
  lastPayment: string;
  nextExpected: string;
  annualizedYield: number;
  averagePayment: number;
}

interface MonthlyDividend {
  month: string;
  year: number;
  totalGross: number;
  totalNet: number;
  totalTax: number;
  paymentsCount: number;
  stockSymbols: string[];
}

const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const DividendsPage: React.FC = () => {
  const [stocks, setStocks] = useState<StockDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDividend, setEditingDividend] = useState<DividendDto | null>(null);
  // Stock the new dividend goes to; used when the dialog adds instead of editing
  const [addStockId, setAddStockId] = useState('');
  const [editForm, setEditForm] = useState({
    date: '',
    grossAmount: '',
    taxPaid: '',
    amount: ''
  });

  useEffect(() => {
    loadDividendData();
  }, []);

  const loadDividendData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const allStocks = await getStocks();
      setStocks(allStocks);
      
    } catch (err) {
      console.error('Error loading dividend data:', err);
      setError('Não foi possível carregar os dados de dividendos');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDividend = (dividend: DividendDto) => {
    setEditingDividend(dividend);
    setEditForm({
      date: dividend.date.split('T')[0], // Convert to YYYY-MM-DD format for input
      grossAmount: dividend.grossAmount.toString(),
      taxPaid: dividend.taxPaid.toString(),
      amount: dividend.amount.toString()
    });
    setEditDialogOpen(true);
  };

  const handleAddDividend = () => {
    const today = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    setEditingDividend(null);
    setAddStockId(stocks[0]?.publicId ?? '');
    setEditForm({
      date: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
      grossAmount: '',
      taxPaid: '0',
      amount: ''
    });
    setEditDialogOpen(true);
  };

  const handleEditFormSubmit = async () => {
    if (!editingDividend && !addStockId) return;

    try {
      setLoading(true);
      const grossAmount = parseFloat(editForm.grossAmount);
      const taxPaid = parseFloat(editForm.taxPaid) || 0;
      const values = {
        date: editForm.date,
        grossAmount,
        taxPaid,
        // Net amount left empty: gross minus tax
        amount: editForm.amount === '' ? grossAmount - taxPaid : parseFloat(editForm.amount)
      };

      if (editingDividend) {
        await updateDividend(editingDividend.publicId, { ...editingDividend, ...values });
      } else {
        await createDividend(addStockId, values);
      }
      
      // Reload data to reflect changes
      await loadDividendData();
      
      // Close dialog
      setEditDialogOpen(false);
      setEditingDividend(null);
      
    } catch (err) {
      console.error('Error saving dividend:', err);
      setError('Não foi possível guardar o dividendo');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDividend = async (dividend: DividendDto) => {
    if (!window.confirm('Tem a certeza de que quer eliminar este dividendo?')) return;

    try {
      setLoading(true);
      await deleteDividend(dividend.publicId);
      
      // Reload data to reflect changes
      await loadDividendData();
      
    } catch (err) {
      console.error('Error deleting dividend:', err);
      setError('Não foi possível eliminar o dividendo');
    } finally {
      setLoading(false);
    }
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const dividendAnalysis = useMemo((): DividendAnalysis[] => {
    return stocks
      .filter(stock => stock.dividends.length > 0)
      .map(stock => {
        const dividends = stock.dividends;
        const totalGross = dividends.reduce((sum, div) => sum + div.grossAmount, 0);
        const totalNet = dividends.reduce((sum, div) => sum + div.amount, 0);
        const totalTax = dividends.reduce((sum, div) => sum + div.taxPaid, 0);
        const paymentsCount = dividends.length;
        
        const sortedDividends = [...dividends].sort((a, b) => 
          new Date(b.date).getTime() - new Date(a.date).getTime()
        );
        
        const lastPayment = sortedDividends.length > 0 ? sortedDividends[0].date : '';
        
        // Calculate annualized yield based on current holdings
        const currentShares = stock.purchases.reduce((sum, purchase) => sum + purchase.quantity, 0);
        const currentValue = currentShares * (stock.currentPrice || 0);
        const annualDividends = totalNet; // Simplified - should be last 12 months
        const annualizedYield = currentValue > 0 ? (annualDividends / currentValue) * 100 : 0;
        
        const averagePayment = paymentsCount > 0 ? totalNet / paymentsCount : 0;
        
        return {
          symbol: stock.symbol,
          totalGross,
          totalNet,
          totalTax,
          paymentsCount,
          lastPayment,
          nextExpected: '', // Would need external API for dividend calendar
          annualizedYield,
          averagePayment
        };
      })
      .sort((a, b) => b.totalNet - a.totalNet);
  }, [stocks]);

  const monthlyDividends = useMemo((): MonthlyDividend[] => {
    const monthlyMap = new Map<string, {
      month: string;
      year: number;
      totalGross: number;
      totalNet: number;
      totalTax: number;
      paymentsCount: number;
      stocks: Set<string>;
    }>();
    
    stocks.forEach(stock => {
      stock.dividends.forEach(dividend => {
        const date = new Date(dividend.date);
        const year = date.getFullYear();
        const month = MONTHS_PT[date.getMonth()];
        const key = `${year}-${month}`;
        
        if (!monthlyMap.has(key)) {
          monthlyMap.set(key, {
            month,
            year,
            totalGross: 0,
            totalNet: 0,
            totalTax: 0,
            paymentsCount: 0,
            stocks: new Set<string>()
          });
        }

        const monthlyData = monthlyMap.get(key)!;
        monthlyData.totalGross += dividend.grossAmount;
        monthlyData.totalNet += dividend.amount;
        monthlyData.totalTax += dividend.taxPaid;
        monthlyData.paymentsCount += 1;
        monthlyData.stocks.add(stock.symbol);
      });
    });
    
    return Array.from(monthlyMap.values())
      .map(m => ({
        month: m.month,
        year: m.year,
        totalGross: m.totalGross,
        totalNet: m.totalNet,
        totalTax: m.totalTax,
        paymentsCount: m.paymentsCount,
        stockSymbols: Array.from(m.stocks)
      }))
      .filter(item => item.year === selectedYear)
      .sort((a, b) => {
        const monthOrder = MONTHS_PT;

        // Sort by most recent month first (descending)
        return monthOrder.indexOf(b.month) - monthOrder.indexOf(a.month);
      });
  }, [stocks, selectedYear]);

  const allDividends = useMemo(() => {
    const allDivs: (DividendDto & { symbol: string })[] = [];
    stocks.forEach(stock => {
      stock.dividends.forEach(dividend => {
        allDivs.push({ ...dividend, symbol: stock.symbol });
      });
    });
    return allDivs.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [stocks]);

  const totalDividends = useMemo(() => {
    const total = allDividends.reduce((sum, div) => sum + div.amount, 0);
    const totalGross = allDividends.reduce((sum, div) => sum + div.grossAmount, 0);
    const totalTax = allDividends.reduce((sum, div) => sum + div.taxPaid, 0);
    return { total, totalGross, totalTax };
  }, [allDividends]);

  const formatCurrency = (amount: number): string => formatEuro(amount);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    stocks.forEach(stock => {
      stock.dividends.forEach(dividend => {
        years.add(new Date(dividend.date).getFullYear());
      });
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [stocks]);

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Carteira de dividendos"
        subtitle="Acompanhe pagamentos de dividendos, rendimentos e tendências de receitas"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddDividend}
            disabled={stocks.length === 0}
          >
            Adicionar dividendo
          </Button>
        }
      />

      {/* Summary Cards */}
      <AutoGrid min={150} sx={{ mb: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AttachMoneyIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Total de dividendos líquidos
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {formatCurrency(totalDividends.total)}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AccountBalanceIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Total de imposto pago
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {formatCurrency(totalDividends.totalTax)}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TimelineIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Ações com dividendos
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="30%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {dividendAnalysis.length}
              </Typography>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Receita mensal média
              </Typography>
            </Box>
            {loading ? (
              <Skeleton variant="text" width="60%" height={32} />
            ) : (
              <Typography variant="h5" component="div">
                {formatCurrency(totalDividends.total / 12)}
              </Typography>
            )}
          </CardContent>
        </Card>
      </AutoGrid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="separadores de dividendos" variant="scrollable" scrollButtons="auto">
          <Tab label="Por ação" />
          <Tab label="Vista mensal" />
          <Tab label="Histórico de pagamentos" />
        </Tabs>

        <TabPanel value={tabValue} index={0} sx={{ p: 3 }}>
          {/* By Stock Analysis */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Símbolo</TableCell>
                  <TableCell align="right">Total líquido</TableCell>
                  <TableCell align="right">Total de imposto</TableCell>
                  <TableCell align="right">Pagamentos</TableCell>
                  <TableCell align="right">Pagamento médio</TableCell>
                  <TableCell align="right">Rendimento %</TableCell>
                  <TableCell align="right">Último pagamento</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 7 }).map((_, cellIndex) => (
                        <TableCell key={cellIndex}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : dividendAnalysis.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Box sx={{ py: 4 }}>
                        <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          Nenhum dividendo encontrado
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Adicione pagamentos de dividendos para acompanhar a sua receita
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  dividendAnalysis.map((analysis) => (
                    <TableRow key={analysis.symbol} hover>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {analysis.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(analysis.totalNet)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(analysis.totalTax)}
                      </TableCell>
                      <TableCell align="right">
                        {analysis.paymentsCount}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(analysis.averagePayment)}
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${formatNumber(analysis.annualizedYield)}%`}
                          color={analysis.annualizedYield > 3 ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {analysis.lastPayment ? formatDate(analysis.lastPayment) : 'N/D'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1} sx={{ p: 3 }}>
          {/* Monthly View */}
          <Box sx={{ mb: 3 }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Ano</InputLabel>
              <Select
                value={selectedYear}
                label="Ano"
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Mês</TableCell>
                  <TableCell align="right">Valor bruto</TableCell>
                  <TableCell align="right">Imposto pago</TableCell>
                  <TableCell align="right">Valor líquido</TableCell>
                  <TableCell align="right">Pagamentos</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthlyDividends.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Nenhum pagamento de dividendos encontrado em {selectedYear}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  monthlyDividends.map((monthly) => (
                    <TableRow key={`${monthly.year}-${monthly.month}`}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {monthly.month} {monthly.year}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(monthly.totalGross)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(monthly.totalTax)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(monthly.totalNet)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={monthly.stockSymbols.length ? monthly.stockSymbols.join(', ') : 'Sem ações'}>
                          <span>{monthly.paymentsCount} ({monthly.stockSymbols.length})</span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={2} sx={{ p: 3 }}>
          {/* Payment History */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Símbolo</TableCell>
                  <TableCell align="right">Valor bruto</TableCell>
                  <TableCell align="right">Imposto pago</TableCell>
                  <TableCell align="right">Valor líquido</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, index) => (
                    <TableRow key={index}>
                      {Array.from({ length: 6 }).map((_, cellIndex) => (
                        <TableCell key={cellIndex}><Skeleton /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : allDividends.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Nenhum pagamento de dividendos encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  allDividends.slice(0, 50).map((dividend) => (
                    <TableRow key={dividend.publicId} hover>
                      <TableCell>
                        {formatDate(dividend.date)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {dividend.symbol}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(dividend.grossAmount)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(dividend.taxPaid)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(dividend.amount)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          size="small"
                          onClick={() => handleEditDividend(dividend)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handleDeleteDividend(dividend)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>

      {/* Edit Dividend Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingDividend ? 'Editar dividendo' : 'Adicionar dividendo'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!editingDividend && (
              <FormControl fullWidth>
                <InputLabel id="add-dividend-stock">Ação</InputLabel>
                <Select
                  labelId="add-dividend-stock"
                  label="Ação"
                  value={addStockId}
                  onChange={(e) => setAddStockId(e.target.value as string)}
                >
                  {stocks.map(stock => (
                    <MenuItem key={stock.publicId} value={stock.publicId}>{stock.symbol}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Data"
              type="date"
              value={editForm.date}
              onChange={(e) => handleEditFormChange('date', e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Valor bruto"
              type="number"
              value={editForm.grossAmount}
              onChange={(e) => handleEditFormChange('grossAmount', e.target.value)}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
            <TextField
              label="Imposto pago"
              type="number"
              value={editForm.taxPaid}
              onChange={(e) => handleEditFormChange('taxPaid', e.target.value)}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
            <TextField
              label="Valor líquido"
              type="number"
              value={editForm.amount}
              onChange={(e) => handleEditFormChange('amount', e.target.value)}
              fullWidth
              inputProps={{ step: "0.01" }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleEditFormSubmit} 
            variant="contained"
            disabled={loading || !editForm.date || editForm.grossAmount === '' || (!editingDividend && !addStockId)}
          >
            {editingDividend ? 'Guardar alterações' : 'Adicionar dividendo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DividendsPage;
